import React, { useEffect } from 'react';
import { makeStyles } from '@material-ui/core';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Content,
  Header,
  Page,
  TableColumn,
} from '@backstage/core-components';
import {
  CatalogFilterLayout,
  EntityKindPicker,
  EntityLifecyclePicker,
  EntityListProvider,
  EntityOwnerPicker,
  EntityRefLink,
  EntitySearchBar,
  EntityTagPicker,
  EntityTypePicker,
} from '@backstage/plugin-catalog-react';
import { CatalogTable, CatalogTableRow } from '@backstage/plugin-catalog';

const useStyles = makeStyles({
  header: {
    backgroundColor: '#162746 !important',
    backgroundImage: 'none !important',
    '&::before, &::after': {
      background: 'transparent !important',
      backgroundImage: 'none !important',
    },
    '& [class*="BackstageHeader"], & [class*="PageTheme"], & svg': {
      background: '#162746 !important',
      backgroundImage: 'none !important',
    },
    '& svg': {
      display: 'none !important',
    },
  },
});

function getKindFromSearch(search: string): string | undefined {
  const params = new URLSearchParams(search);

  // Backstage uses decoded keys like filters[kind]
  const kind = params.get('filters[kind]');
  return kind ?? undefined;
}

export const CatalogPage = () => {
  const classes = useStyles();
  const { search } = useLocation();
  const navigate = useNavigate();
  const toComparable = (value: unknown): string => String(value ?? '').toLowerCase();
  const compareBy = (
    a: CatalogTableRow,
    b: CatalogTableRow,
    selector: (row: CatalogTableRow) => unknown,
  ): number => toComparable(selector(a)).localeCompare(toComparable(selector(b)));

  const getName = ({ entity }: CatalogTableRow) => entity.metadata.name ?? '';
  const getKind = ({ entity }: CatalogTableRow) => entity.kind ?? '';
  const getType = ({ entity }: CatalogTableRow) => (entity as any).spec?.type ?? '';
  const getOwner = ({ entity }: CatalogTableRow) => (entity as any).spec?.owner ?? '';
  const getLifecycle = ({ entity }: CatalogTableRow) =>
    (entity as any).spec?.lifecycle ?? '';
  const getDescription = ({ entity }: CatalogTableRow) =>
    entity.metadata.description ?? '';

  const columns: TableColumn<CatalogTableRow>[] = [
    {
      title: 'Name',
      field: 'entity.metadata.name',
      customSort: (a, b) => compareBy(a, b, getName),
      render: ({ entity }) => (
        <strong>
          <EntityRefLink entityRef={entity} />
        </strong>
      ),
    },
    {
      title: 'Kind',
      field: 'entity.kind',
      customSort: (a, b) => compareBy(a, b, getKind),
      render: ({ entity }) => entity.kind ?? '—',
    },
    {
      title: 'Type',
      field: 'entity.spec.type',
      customSort: (a, b) => compareBy(a, b, getType),
      render: ({ entity }) => (entity as any).spec?.type ?? '—',
    },
    {
      title: 'Owner',
      field: 'entity.spec.owner',
      customSort: (a, b) => compareBy(a, b, getOwner),
      render: ({ entity }) => {
        const owner = (entity as any).spec?.owner;
        if (!owner) return '—';
        const ownerStr = String(owner);
        if (!ownerStr.includes(':')) return ownerStr;
        return <EntityRefLink entityRef={ownerStr} />;
      },
    },
    {
      title: 'Lifecycle',
      field: 'entity.spec.lifecycle',
      customSort: (a, b) => compareBy(a, b, getLifecycle),
      render: ({ entity }) => (entity as any).spec?.lifecycle ?? '—',
    },
    {
      title: 'Description',
      field: 'entity.metadata.description',
      customSort: (a, b) => compareBy(a, b, getDescription),
      render: ({ entity }) => entity.metadata.description ?? '—',
    },
  ];

  useEffect(() => {
    const kind = getKindFromSearch(search)?.toLowerCase();

    if (kind === 'api') {
      // Preserve other filters (owner/user/etc.) by forwarding the query string
      navigate(`/api-docs${search}`, { replace: true });
    }
  }, [search, navigate]);

  return (
    <Page>
      <Header className={classes.header} title="Catalog" />
      <Content>
        <EntityListProvider>
          <CatalogFilterLayout>
            <CatalogFilterLayout.Filters>
              <EntitySearchBar />
              <EntityKindPicker allowedKinds={['component', 'system']} />
              <EntityTypePicker />
              <EntityOwnerPicker />
              <EntityLifecyclePicker />
              <EntityTagPicker />
            </CatalogFilterLayout.Filters>

            <CatalogFilterLayout.Content>
              <CatalogTable columns={columns} />
            </CatalogFilterLayout.Content>
          </CatalogFilterLayout>
        </EntityListProvider>
      </Content>
    </Page>
  );
};
